import React, { useState } from 'react';
import { Upload, Button, Card, Table, Select, Tag, message, Typography, Space } from 'antd';
import { UploadOutlined, CopyOutlined } from '@ant-design/icons';
import type { UploadProps } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import { GET_FILE_UPLOAD_URL } from '../../../utils/storageUtils';
import {
    RECORD_SUPPORT_UPLOAD,
    LIST_SUPPORT_UPLOAD_RECORDS,
    RecordSupportUploadResponse,
    RecordSupportUploadVariables,
    ListSupportUploadRecordsResponse,
    ListSupportUploadRecordsVariables,
    SupportUploadRecord,
} from './graphql/supportUpload';

const { Text, Title } = Typography;

const CATEGORY_COLOR: Record<string, string> = {
    VIDEO: 'magenta',
    IMAGE: 'green',
    DOCUMENT: 'blue',
    AUDIO: 'orange',
    OTHER: 'default',
};

const UploadToolPage: React.FC = () => {
    const { t } = useTranslation();
    const [uploading, setUploading] = useState(false);
    const [categoryFilter, setCategoryFilter] = useState<string | undefined>(undefined);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);

    const [getUploadUrl] = useMutation(GET_FILE_UPLOAD_URL);
    const [recordUpload] = useMutation<RecordSupportUploadResponse, RecordSupportUploadVariables>(RECORD_SUPPORT_UPLOAD);

    // 上传使用的 usage（作为 objectKey 前缀，直接为 support-upload，不带 general/）
    const UPLOAD_USAGE = 'support-upload';

    const { data, loading, refetch } = useQuery<ListSupportUploadRecordsResponse, ListSupportUploadRecordsVariables>(
        LIST_SUPPORT_UPLOAD_RECORDS,
        {
            variables: { fileCategory: categoryFilter ?? null, pageRequest: { page, size: pageSize } },
            fetchPolicy: 'network-only',
        },
    );

    const listing = data?.listSupportUploadRecords;

    // 选文件后自定义上传：私有桶直传 R2 → 记录到后端
    // 上传大小上限：100MB
    const MAX_UPLOAD_SIZE = 100 * 1024 * 1024;
    const beforeUpload = (file: File) => {
        if (file.size > MAX_UPLOAD_SIZE) {
            message.error(t('supportTools.upload.fileTooLarge'));
            return Upload.LIST_IGNORE;
        }
        return true;
    };

    const customUpload: UploadProps['customRequest'] = async (options) => {
        const file = options.file as File;
        setUploading(true);
        try {
            // 1) 取预签名上传 URL（usage=support-upload → objectKey 前缀直接为 support-upload/）
            const { data: urlData } = await getUploadUrl({
                variables: { input: { filename: file.name, contentType: file.type || 'application/octet-stream', usage: UPLOAD_USAGE } },
            });
            const uploadUrl: string | undefined = urlData?.getFileUploadUrl?.uploadUrl;
            const objectKey: string | undefined = urlData?.getFileUploadUrl?.objectKey;
            if (!uploadUrl || !objectKey) throw new Error('Failed to get upload URL');

            // 2) 直传 R2（私有桶）
            // 注意：content-type 必须用 CORS safelisted 值 multipart/form-data，
            // 否则会触发 preflight 的 Access-Control-Request-Headers 检查，而 R2 桶 CORS 未放行 content-type 头会被拦
            const putRes = await fetch(uploadUrl, {
                method: 'PUT',
                body: file,
                headers: { 'content-type': 'multipart/form-data' },
            });
            if (!putRes.ok) throw new Error('Failed to upload file');

            // 3) 记录到后端
            await recordUpload({
                variables: {
                    input: {
                        objectKey,
                        fileName: file.name,
                        contentType: file.type || null,
                        bucketType: 0, // 私有桶
                        usage: UPLOAD_USAGE,
                    },
                },
            });
            message.success(t('supportTools.upload.uploadSuccess'));
            options.onSuccess?.({}, new XMLHttpRequest());
            setPage(1);
            await refetch();
        } catch (e: any) {
            console.error('support upload failed:', e);
            message.error(e?.message || t('supportTools.upload.uploadFailed'));
            options.onError?.(e);
        } finally {
            setUploading(false);
        }
    };

    const handleCopy = async (objectKey: string) => {
        try {
            await navigator.clipboard.writeText(objectKey);
            message.success(t('supportTools.upload.copied'));
        } catch {
            message.error(t('supportTools.upload.copyFailed'));
        }
    };

    const columns: ColumnsType<SupportUploadRecord> = [
        { title: t('supportTools.upload.col.fileName'), dataIndex: 'fileName', key: 'fileName', width: 220 },
        {
            title: t('supportTools.upload.col.category'),
            dataIndex: 'fileCategory',
            key: 'fileCategory',
            width: 110,
            render: (c: string) => <Tag color={CATEGORY_COLOR[c] ?? 'default'}>{c}</Tag>,
        },
        {
            title: t('supportTools.upload.col.objectKey'),
            dataIndex: 'objectKey',
            key: 'objectKey',
            render: (k: string) => (
                <Space size={4}>
                    <Text code copyable={false} style={{ wordBreak: 'break-all' }}>{k}</Text>
                    <Button type="text" size="small" icon={<CopyOutlined />} onClick={() => handleCopy(k)} />
                </Space>
            ),
        },
        { title: t('supportTools.upload.col.usage'), dataIndex: 'usage', key: 'usage', width: 160, render: (u?: string) => u || '-' },
        { title: t('supportTools.upload.col.operator'), dataIndex: 'operatorName', key: 'operatorName', width: 120, render: (o?: string) => o || '-' },
        {
            title: t('supportTools.upload.col.createdAt'),
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 180,
            render: (ts?: number) => (ts ? dayjs.unix(ts).format('YYYY-MM-DD HH:mm:ss') : '-'),
        },
    ];

    return (
        <div className="h-full w-full flex flex-col gap-4">
            <div className="rounded-2xl bg-gradient-to-r from-purple-600 to-indigo-600 px-8 py-8 text-white shadow-sm">
                <Title level={2} style={{ margin: 0, color: 'white' }}>
                    {t('supportTools.upload.title')}
                </Title>
                <div className="mt-2 text-white/90 text-base">
                    {t('supportTools.upload.privateBucketHint')}
                </div>
            </div>

            <Card className="rounded-2xl shadow-sm">
                <div className="flex flex-col gap-4">
                    <div className="flex flex-wrap items-end justify-between gap-4">
                        <div className="flex flex-col gap-2 min-w-[200px]">
                            <Text className="text-base font-medium">{t('supportTools.upload.filterCategory')}</Text>
                            <Select
                                allowClear
                                className="w-full min-w-[200px]"
                                placeholder={t('supportTools.upload.filterCategory')}
                                value={categoryFilter}
                                onChange={(v) => { setCategoryFilter(v); setPage(1); }}
                                options={['VIDEO', 'IMAGE', 'DOCUMENT', 'AUDIO', 'OTHER'].map((c) => ({ label: c, value: c }))}
                            />
                        </div>
                        <Upload customRequest={customUpload} beforeUpload={beforeUpload} showUploadList={false} maxCount={1}>
                            <Button type="primary" size="large" icon={<UploadOutlined />} loading={uploading}>
                                {t('supportTools.upload.uploadButton')}
                            </Button>
                        </Upload>
                    </div>

                    <Table<SupportUploadRecord>
                        rowKey="id"
                        columns={columns}
                        dataSource={listing?.content ?? []}
                        loading={loading}
                        size="small"
                        pagination={{
                            current: page,
                            pageSize,
                            total: listing?.totalElements ?? 0,
                            showSizeChanger: true,
                            onChange: (p, s) => { setPage(p); setPageSize(s || 20); },
                        }}
                    />
                </div>
            </Card>
        </div>
    );
};

export default UploadToolPage;
